/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { Button, Card, CardContent, Combobox } from "@repo/ui";
import { useGetUsersByMultipleRoles } from "utils/apis/lookups.api";
import { useValidateFieldTeam } from "utils/apis/daily-operations";
import { showToast } from "utils/helper";

// --- Mock Initial Data ---
const INITIAL_DATA = {
  todo: [
    { id: "1", title: "Research Kendo UI components" },
    { id: "2", title: "Style shadcn buttons" },
  ],
  inProgress: [{ id: "3", title: "Configure DND sensors" }],
  done: [{ id: "4", title: "Install React" }],
};

// --- Sortable Item Component (The Draggable Card) ---
function SortableItem({ id, item, onRoleChange, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative mb-3 flex items-center gap-3 rounded-lg border bg-white p-4 shadow-sm hover:border-blue-400"
    >
      <button
        {...listeners}
        {...attributes}
        className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing"
      >
        <GripVertical size={18} />
      </button>

      <div className="flex flex-col w-full">
        <div className="flex flex-row justify-between gap-4">
          <p style={{ fontWeight: 500 }}> Name</p>
          <p className="ml-auto">{item?.name}</p>
        </div>
        <div className="border-b my-2 bg-accent" />
        <div className="flex flex-row justify-between gap-4">
          <p style={{ fontWeight: 500 }}> Employee ID</p>
          <p className="ml-auto">{item?.employeeId}</p>
        </div>

        <div className="border-b my-2 bg-accent" />

        <div className="flex flex-row justify-between gap-4">
          <p style={{ fontWeight: 500 }}> Nationality</p>
          <p className="ml-auto">{item?.nationality}</p>
        </div>
        <div className="border-b my-2 bg-accent" />
        <div className="flex flex-row justify-between gap-4 items-center justify-center">
          <p style={{ fontWeight: 500, marginRight: "20%" }}> Role</p>
          <Combobox
            setValue={(e) => {
              onRoleChange(e);
            }}
            value={item?.role}
            placeholder="Select Route"
            data={[
              { label: "Team Leader", value: "Team Leader" },
              { label: "Driver", value: "Driver" },
              { label: "Guard", value: "Guard" },
            ]}
          />
        </div>
        <div className="flex flex-row justify-end mt-2">
          <Button
            variant={"secondary"}
            onClick={() => {
              onDelete();
            }}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- Main Kanban Board Component ---
export default function KanbanBoard({
  columnsRef,
  users,
  cashCenterId,
  operationDate,
  routeA,
  routeB,
}) {
  const [columns, setColumns] = useState({
    routeA:
      routeA?.fieldTeam.map((elm, index) => ({
        ...elm,

        id: `routeA_${index}`,
      })) || [],
    routeB:
      routeB?.fieldTeam.map((elm, index) => ({
        ...elm,

        id: `routeB_${index}`,
      })) || [],
  });

  const [addUserRouteA, setAddUserRouteA] = useState("");
  const [addUserRouteB, setAddUserRouteB] = useState("");
  const { mutateAsync: validateFieldTeam } = useValidateFieldTeam();

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Helper to find which column an item belongs to
  const findContainer = (id) => {
    if (id in columns) return id;
    return Object.keys(columns).find((key) =>
      columns[key].find((item) => item.id === id),
    );
  };

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  function handleDragOver(event) {
    const { active, over } = event;
    const overId = over?.id;

    if (!overId || active.id === overId) return;

    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer || activeContainer === overContainer)
      return;

    setColumns((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];

      const activeIndex = activeItems.findIndex((i) => i.id === active.id);
      const overIndex = overItems.findIndex((i) => i.id === overId);

      let newIndex;
      if (overId in prev) {
        newIndex = overItems.length + 1;
      } else {
        const isBelowLastItem = over && overIndex === overItems.length - 1;
        const modifier = isBelowLastItem ? 1 : 0;
        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
      }

      return {
        ...prev,
        [activeContainer]: prev[activeContainer].filter(
          (i) => i.id !== active.id,
        ),
        [overContainer]: [
          ...prev[overContainer].slice(0, newIndex),
          prev[activeContainer][activeIndex],
          ...prev[overContainer].slice(newIndex),
        ],
      };
    });
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(over?.id);

    if (activeContainer && overContainer && activeContainer === overContainer) {
      const activeIndex = columns[activeContainer].findIndex(
        (i) => i.id === active.id,
      );
      const overIndex = columns[overContainer].findIndex(
        (i) => i.id === over?.id,
      );

      if (activeIndex !== overIndex) {
        setColumns((items) => ({
          ...items,
          [overContainer]: arrayMove(
            items[overContainer],
            activeIndex,
            overIndex,
          ),
        }));
      }
    }
    setActiveId(null);
  }
  const activeMember = useMemo(() => {
    if (activeId === null || activeId === undefined) {
      return {};
    }
    return Object.values(columns)
      .flat()
      .find((elm) => elm.id === activeId);
  }, [activeId]);

  return (
    <div className="min-h-screen bg-slate-50 p-10">
      <div className="flex gap-[16px]">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {Object.keys(columns).map((id) => (
            <div
              key={id}
              className="flex flex-col "
              style={{ width: `calc(50% - 8px)` }}
            >
              <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-100/50 p-4 min-h-[500px]">
                <div className="flex flex-row gap-2 items-end justify-end">
                  <div className="flex flex-col w-full">
                    <p
                      style={{
                        marginBottom: "4px",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Add Member
                    </p>
                    <Combobox
                      placeholder="Add member"
                      value={
                        id === "routeA"
                          ? addUserRouteA
                          : id === "routeB"
                            ? addUserRouteB
                            : ""
                      }
                      setValue={(e) => {
                        if (id === "routeA") {
                          setAddUserRouteA(e);
                        } else if (id === "routeB") {
                          setAddUserRouteB(e);
                        }
                      }}
                      data={
                        users?.map((elm) => ({
                          label: `${elm.name}-${elm.employeeId}`,
                          value: JSON.stringify(elm),
                        })) || []
                      }
                    />
                  </div>

                  <Button
                    disabled={!addUserRouteA}
                    variant={"secondary"}
                    onClick={async () => {
                      if (id === "routeA" && addUserRouteA) {
                        const _columns = { ...columns };

                        const _user = JSON.parse(addUserRouteA);
                        const _currentColumn = columns.routeA;
                        const payload = {
                          cashCenterId,
                          operationDate,
                          routeNo: routeA.routeNo,
                          shift: "morning",
                          assigneeId: _user.employeeId,
                        };
                        const isValid = await validateFieldTeam({ ...payload });
                        if (!isValid) {
                          _currentColumn.push({
                            id: `newRouteA_${_currentColumn.length + 1}`,
                            employeeId: _user.employeeId,
                            name: _user.name,
                            nationality: _user.nationality,
                            regulatoryId: _user.regulatoryId,
                          });
                          _columns.routeA = _currentColumn;
                          setColumns(_columns);
                          setAddUserRouteA("");
                        } else {
                          showToast(
                            "error",
                            "This team member can not be assigned",
                          );
                        }
                      } else if (id === "routeB" && addUserRouteB) {
                        const _columns = { ...columns };
                        const _user = JSON.parse(addUserRouteB);
                        const _currentColumn = columns.routeB;

                        const payload = {
                          cashCenterId,
                          operationDate,
                          routeNo: routeA.routeNo,
                          shift: "morning",
                          assigneeId: _user.employeeId,
                        };
                        const isValid = await validateFieldTeam({ ...payload });
                        if (!isValid) {
                          _currentColumn.push({
                            id: `newRouteB_${_currentColumn.length + 1}`,
                            employeeId: _user.employeeId,
                            name: _user.name,
                            nationality: _user.nationality,
                            regulatoryId: _user.regulatoryId,
                          });
                          _columns.routeB = _currentColumn;
                          setColumns(_columns);
                          setAddUserRouteB("");
                        } else {
                          showToast(
                            "error",
                            "This team member can not be assigned",
                          );
                        }
                      }
                    }}
                  >
                    Add
                  </Button>
                </div>

                <SortableContext
                  items={columns[id].map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {columns[id].map((task, index) => (
                    <SortableItem
                      key={task.id}
                      id={task.id}
                      item={task}
                      onDelete={() => {
                        const _columns = { ...columns };
                        const _currentColumn = columns[id];
                        _currentColumn.splice(index, 1);
                        _columns[id] = _currentColumn;
                        setColumns(_columns);
                      }}
                      onRoleChange={(e) => {
                        const _columns = { ...columns };
                        const _currentColumn = columns[id];
                        _currentColumn[index].role = e;
                        _columns[id] = _currentColumn;
                        setColumns(_columns);
                      }}
                    />
                  ))}
                </SortableContext>
              </div>
            </div>
          ))}

          <DragOverlay
            dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: { active: { opacity: "0.4" } },
              }),
            }}
          >
            {activeId ? (
              <div className="flex items-center gap-3 rounded-lg border border-blue-500 bg-white p-4 shadow-xl ring-2 ring-blue-500/20">
                <GripVertical size={18} className="text-blue-500" />
                <div className="flex flex-col">
                  <div className="flex flex-row justify-between gap-4">
                    <p style={{ fontWeight: 500 }}> Name</p>
                    <p className="ml-auto">{activeMember.name}</p>
                  </div>
                  <div className="border-b my-2 bg-accent" />
                  <div className="flex flex-row justify-between gap-4">
                    <p style={{ fontWeight: 500 }}> Employee ID</p>
                    <p className="ml-auto">{activeMember.employeeId}</p>
                  </div>

                  <div className="border-b my-2 bg-accent" />

                  <div className="flex flex-row justify-between gap-4">
                    <p style={{ fontWeight: 500 }}> Nationality</p>
                    <p className="ml-auto">{activeMember.nationality}</p>
                  </div>
                  <div className="border-b my-2 bg-accent" />
                  <div className="flex flex-col justify-between gap-2">
                    <p style={{ fontWeight: 500 }}> Role</p>
                    <Combobox
                      setValue={(e) => {
                        // setSwapRouteB(e);
                        // mergedTeam(swapRouteA, e);
                      }}
                      value={activeMember.role}
                      placeholder="Select Route"
                      data={[
                        { label: "Team Leader", value: "Team Leader" },
                        { label: "Driver", value: "Driver" },
                        { label: "Guard", value: "Guard" },
                      ]}
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}
